import { useMemo } from 'react';

import * as THREE from 'three';

import { IMeasurement } from '@core';
import { Data3d } from '@store';

import { getMeasurementValuesByName } from './helpers';
import { type ParamAxisLock, LOCK_TYPE, FocusedMeasurement } from './types';
import { getIntrinsicVisualizationFrame } from './utils/rotationUtils';

type AngleDirections = {
    referenceDirection: THREE.Vector3;
    actualDirection: THREE.Vector3;
};
/**
 * Compute the reference and actual directions for intrinsic angle visualization.
 * Both are computed from Euler angles to ensure displayed angle matches measurement.
 * Returns undefined if not an intrinsic lock or if Euler angles aren't available.
 */

export function computeAngleDirections(
    lock: ParamAxisLock | undefined,
    measurements: IMeasurement[] | undefined,
    frame: number,
): AngleDirections | undefined {
    if (!lock || lock.type !== LOCK_TYPE.INTRINSIC) return undefined;

    const eulerAngles = getMeasurementValuesByName(measurements, lock.measurementName, frame) as
        | [number, number, number]
        | null;

    if (!eulerAngles) return undefined;

    const { referenceDirection, actualDirection } = getIntrinsicVisualizationFrame(eulerAngles, lock.axisIndex);
    return { referenceDirection, actualDirection };
}

export const useAngleDirections = (currentFocus: FocusedMeasurement, data: Data3d | null, currentFrame = 0) => {
    return useMemo(() => {
        return computeAngleDirections(currentFocus?.axisData.camera, data?.measurements, currentFrame);
    }, [currentFocus?.axisData.camera, currentFrame, data?.measurements]);
};
